/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
// @ts-ignore: side-effect CSS import without type declarations
import 'devextreme/dist/css/dx.light.css';
import DataGrid, {
  Column,
  FilterRow,
  HeaderFilter,
  FilterPanel,
  Sorting,
  Grouping,
  GroupPanel,
  RemoteOperations,
  Scrolling,
  Summary,
  GroupItem,
  ColumnFixing,
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import { locale, loadMessages } from 'devextreme/localization';
import esMessages from 'devextreme/localization/messages/es.json';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Scenarios } from '../constants/scenarios';
import {
  GridConfig,
  getActiveGroup,
  getConfiguration,
  setGridGrouping,
  updateGroup,
  vflexSaveFieldPosition,
  vflexSaveFieldWidth,
  vflexSaveFieldsConfig,
} from '../store/slices/account-entries';
import { RootState } from '../store';
import { useSelector, useDispatch } from 'react-redux';
import GridConfigModal from './GridConfigModal';
import { createRoot } from 'react-dom/client';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import { GroupSelectorModal } from './GroupSelectorModal';

loadMessages(esMessages);
locale('es');

export interface LocalGridConfig {
  columnsOrder: string[];
  visible: Record<string, boolean>;
}

export interface AccountingEntriesDataGridProps {
  scenario: Scenarios;
  data: CustomStore | any[] | null;
  onClickSearch?: () => void;
  isConfigAvailable?: boolean;
  isGroupingAvailable?: boolean;
  isFilterPanelAvailable?: boolean;
  onRowContextMenuPreparing?: (e: any) => void;
  operationMode?: 'remote' | 'local';
  localColumns?: any[];
  localConfig?: LocalGridConfig;
  onLocalConfigChange?: (config: LocalGridConfig) => void;
}

const getNumberFormat = () => {
  return (value: number | null | undefined): string => {
    if (value == null) return '';
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    });
  };
};

const arraysEqual = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i]);

export const AccountingEntriesDataGrid: React.FC<AccountingEntriesDataGridProps> = ({
  scenario,
  data,
  onClickSearch,
  isConfigAvailable = true,
  isGroupingAvailable = true,
  isFilterPanelAvailable = true,
  onRowContextMenuPreparing,
  operationMode = 'remote',
  localColumns,
  localConfig,
  onLocalConfigChange,
}) => {
  const isLocal = operationMode === 'local';
  const dispatch = useDispatch();
  const {
    vFlexColumnsConfig,
    vFlexColumnsConfigParts,
    gridConfig,
    activeGroup,
  } = useSelector((state: RootState) => state.accountEntries.scenarios[scenario]);

  const resolvedColumns = useMemo(
    () => (isLocal && localColumns ? localColumns : gridConfig.columns),
    [isLocal, localColumns, gridConfig.columns]
  );


  const [modalOpen, setModalOpen] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);

  const [columnsOrder, setColumnsOrder] = useState<string[]>([]);
  const [groupable, setGroupable] = useState<Record<string, boolean>>({});
  const [summary, setSummary] = useState<Record<string, boolean>>({});
  const [visible, setVisible] = useState<Record<string, boolean>>({});
  const [fixed, setFixed] = useState<Record<string, boolean>>({});

  const [currentGroupFields, setCurrentGroupFields] = useState<string[]>([]);


  const dataGridRef = useRef<any>(null);

  const gridStateKey =
    scenario === Scenarios.LibroDiarioEscenario ? 'gridState' : `gridState_${scenario}`;

  const activeGroupCorrelative = activeGroup?.[0]?.CorrelativoGrupo ?? null;

  const fieldCaptionMap = useMemo(() => {
    const map: Record<string, string> = {};
    resolvedColumns.forEach((col: any) => {
      map[col.dataField] = col.caption || col.dataField;
    });
    return map;
  }, [resolvedColumns]);

  const saveGridStateSync = async (state: GridConfig): Promise<void> => {
    localStorage.setItem(gridStateKey, JSON.stringify(state));

    const columnsVflex = state.columns.map(c => ({
      Campo: c.dataField,
      Posicion: c.visibleIndex ?? 0,
      Visible: c.visible ?? true,
      Sumar: state.summaryColumns?.includes(c.dataField) ?? false,
    })).sort((a, b) => a.Posicion - b.Posicion);

    await vflexSaveFieldsConfig(JSON.stringify(columnsVflex), scenario);
  };

  const loadConfig = useCallback(async () => {
    if (isLocal) {
      const allFields = resolvedColumns.map(c => c.dataField);
      const order = localConfig?.columnsOrder?.length ? localConfig.columnsOrder : allFields;

      setColumnsOrder(order);

      const groupableMap: Record<string, boolean> = {};
      const summaryMap: Record<string, boolean> = {};
      const visibleMap: Record<string, boolean> = {};
      const fixedMap: Record<string, boolean> = {};

      resolvedColumns.forEach(col => {
        groupableMap[col.dataField] = col.dataField !== 'OID';
        summaryMap[col.dataField] = col.summary === true;
        visibleMap[col.dataField] = localConfig?.visible?.[col.dataField] ?? (col.visible !== false);
        fixedMap[col.dataField] = false;
      });

      setGroupable(groupableMap);
      setSummary(summaryMap);
      setVisible(visibleMap);
      setFixed(fixedMap);
      return;
    }

    const cachedState = localStorage.getItem(gridStateKey);
    let savedConfig: GridConfig | null = cachedState ? JSON.parse(cachedState) : null;

    if (vFlexColumnsConfig && vFlexColumnsConfig.length > 0) {
      const order = savedConfig
        ? savedConfig.columns.map(c => c.dataField)
        : gridConfig.columns.map(c => c.dataField);

      setColumnsOrder(order);

      const groupableMap: Record<string, boolean> = {};
      const summaryMap: Record<string, boolean> = {};
      const visibleMap: Record<string, boolean> = {};
      const fixedMap: Record<string, boolean> = {};

      gridConfig.columns.forEach(col => {
        groupableMap[col.dataField] = col.dataField !== 'OID';
        summaryMap[col.dataField] = savedConfig?.summaryColumns?.includes(col.dataField) ?? (col.summary === true);
        visibleMap[col.dataField] = savedConfig?.columns?.find(c => c.dataField === col.dataField)?.visible ?? (col.visible !== false);
        fixedMap[col.dataField] = savedConfig?.columns?.find(c => c.dataField === col.dataField)?.fixed ?? false;
      });

      setGroupable(groupableMap);
      setSummary(summaryMap);
      setVisible(visibleMap);
      setFixed(fixedMap);
    }
  }, [isLocal, localConfig, resolvedColumns, gridStateKey, vFlexColumnsConfig, gridConfig.columns]);

  useEffect(() => {
    if (isLocal || vFlexColumnsConfig !== null) {
      loadConfig();
    }
  }, [isLocal, vFlexColumnsConfig, vFlexColumnsConfigParts, loadConfig]);

  // useEffect(() => {
  //   getConfiguration(scenario);
  // }, [scenario]);

  const lastUserActionRef = useRef<{ fields: string[]; time: number } | null>(null);

  const programmaticGroupRef = useRef<{ fields: string[]; time: number } | null>(null);

  // const persistGroupingTimerRef = useRef<number | null>(null);
  // const latestGroupFieldsRef = useRef<string[]>([]);

  const isPartialEcho = (partial: string[], complete: string[]): boolean =>
    partial.length > 0 &&
    partial.length < complete.length &&
    partial.every((f, i) => f === complete[i]);

  const buildActiveGroupFields = useCallback((): string[] => {
    if (!activeGroup || activeGroup.length === 0) return [];
    return [...activeGroup]
      .sort((a, b) => a.Orden - b.Orden)
      .map(f => f.Campo);
  }, [activeGroup]);

  const applyGrouping = useCallback((fields: string[]) => {
    lastUserActionRef.current = { fields, time: Date.now() };
    programmaticGroupRef.current = { fields, time: Date.now() };
    setCurrentGroupFields(fields);
    dispatch(setGridGrouping({ scenario, fields }));
  }, [dispatch, scenario, activeGroup]);

  useEffect(() => {
    if (isLocal) return;
    if (activeGroup) {
      handleGroupActivated(activeGroup.map(f => f.Campo));
    }
  }, [activeGroup, scenario, isLocal]);

  useEffect(() => {
    if (isLocal || !isGroupingAvailable) {
      setCurrentGroupFields([]);
      return;
    }

    const fields = buildActiveGroupFields();

    const lastAction = lastUserActionRef.current;
    if (lastAction && Date.now() - lastAction.time < 3000) {
      if (lastAction.fields.length === 0 || arraysEqual(lastAction.fields, fields)) {
        setCurrentGroupFields(fields);
        return;
      }
    }

    applyGrouping(fields);
  }, [activeGroup, isGroupingAvailable, buildActiveGroupFields, applyGrouping, vFlexColumnsConfig, isLocal]);



  const handleGroupActivated = useCallback((fields: string[]) => {
    applyGrouping(fields);
  }, [applyGrouping]);

  const handleOptionChanged = useCallback(async (e: any) => {
    // if (isLocal) {
    //   if (e.name === 'columns' && Array.isArray(e.value)) {
    //     const columns = e.value as any[];
    //     const newOrder = columns.map((col: any) => col.dataField).filter(Boolean);
    //     const newVisible: Record<string, boolean> = {};
    //     columns.forEach((col: any) => {
    //       newVisible[col.dataField] = col.visible !== false;
    //     });
    //     setColumnsOrder(newOrder);
    //     setVisible(newVisible);
    //     onLocalConfigChange?.({ columnsOrder: newOrder, visible: newVisible });
    //   }
    //   return;
    // }

    if (e.name === 'grouping' || (e.name === 'columns' && e.fullName?.endsWith('.groupIndex'))) {
      const columns = e.component.getColumns();
      const groupedColumns = columns
        .filter((c: any) => c.groupIndex !== undefined && c.groupIndex !== null)
        .sort((a: any, b: any) => a.groupIndex - b.groupIndex);

      const fields = groupedColumns.map((c: any) => c.dataField);



      const jsonGrouping = JSON.stringify(
        fields.map((field: string, index: number) => ({
          Posicion: index,
          Cabecera: fieldCaptionMap[field] || field,
          Campo: field,
        }))
      )
      await updateGroup({ correlative: 0, jsonFields: jsonGrouping }, scenario)
      

      const programmatic = programmaticGroupRef.current;
      if (programmatic && Date.now() - programmatic.time < 2000) {
        if (arraysEqual(programmatic.fields, fields) || isPartialEcho(fields, programmatic.fields)) {
          setCurrentGroupFields(fields);
          return;
        }
        programmaticGroupRef.current = null;
      }
      await getActiveGroup(scenario);
    }

    if (e.name === 'columns' && e.fullName && e.fullName.endsWith('.width')) {
      const match = e.fullName.match(/columns\[(\d+)\]\.width/);
      if (match) {
        const index = parseInt(match[1], 10);
        const column = e.component.columnOption(index);
        if (column && column.dataField) {
          const newWidth = e.value;
          vflexSaveFieldWidth(column.dataField, newWidth * 10, scenario);

          const cachedState = localStorage.getItem(gridStateKey);
          if (cachedState) {
            const state: GridConfig = JSON.parse(cachedState);
            const colIndex = state.columns.findIndex(c => c.dataField === column.dataField);
            if (colIndex !== -1) {
              state.columns[colIndex].width = newWidth;
              localStorage.setItem(gridStateKey, JSON.stringify(state));
            }
          }
        }
      }
    }

    if (e.name === 'columns' && e.fullName && e.fullName.endsWith('.visibleIndex')) {
      const match = e.fullName.match(/columns\[(\d+)\]\.visibleIndex/);
      if (match) {
        const index = parseInt(match[1], 10);
        const column = e.component.columnOption(index);

        if (column && column.dataField && e.value !== undefined) {
          const fieldName = column.dataField;
          const newTargetIndex = e.value;

          vflexSaveFieldPosition(fieldName, newTargetIndex, scenario);

          const cachedState = localStorage.getItem(gridStateKey);
          if (cachedState) {
            const state: GridConfig = JSON.parse(cachedState);

            state.columns.forEach((c, idx) => {
              const currentGridCol = e.component.columnOption(c.dataField);
              if (currentGridCol && currentGridCol.visibleIndex !== undefined) {
                state.columns[idx].visibleIndex = currentGridCol.visibleIndex;
              }
            });

            localStorage.setItem(gridStateKey, JSON.stringify(state));
          }

          const visibleColumns = e.component.getVisibleColumns();
          setColumnsOrder(visibleColumns.map((col: any) => col.dataField).filter(Boolean));
        }
      }
    }
  }, [dispatch, scenario, isLocal, onLocalConfigChange]);

  const handleSaveConfigFromModal = async (
    newOrder: string[],
    newVisible: Record<string, boolean>,
    newGroupable: Record<string, boolean>,
    newSummary: Record<string, boolean>,
    newFixed: Record<string, boolean>
  ) => {
    const allFields = resolvedColumns.map(col => col.dataField);
    const fullOrder = [...newOrder, ...allFields.filter(f => !newOrder.includes(f))];

    setColumnsOrder(fullOrder);
    setVisible(newVisible);
    setGroupable(newGroupable);
    setSummary(newSummary);
    setFixed(newFixed);

    if (isLocal) {
      onLocalConfigChange?.({ columnsOrder: fullOrder, visible: newVisible });
      setModalOpen(false);
      return;
    }

    const groupIndexMap: Record<string, number> = {};
    gridConfig.columns.forEach((col: any) => {
      if (col.groupIndex !== undefined && col.groupIndex !== null) {
        groupIndexMap[col.dataField] = col.groupIndex;
      }
    });

    const updatedColumns = fullOrder.map((field, idx) => {
      const original = gridConfig.columns.find(c => c.dataField === field);
      return {
        dataField: field,
        visibleIndex: idx,
        visible: newVisible[field] ?? true,
        fixed: newFixed[field] ?? false,
        width: original?.width ?? 100,
        groupIndex: groupIndexMap[field] ?? undefined,
      };
    });

    const summaryColumns = Object.keys(newSummary).filter(field => newSummary[field]);

    const newGridState: GridConfig = {
      ...gridConfig,
      columns: updatedColumns,
      summaryColumns
    };

    await saveGridStateSync(newGridState);
    setModalOpen(false);
    if (data && onClickSearch) onClickSearch();
  };

  // const orderedColumns = useMemo(() => {
  //   if (!isLocal) return resolvedColumns;
  //   const allFields = resolvedColumns.map(c => c.dataField);
  //   const order = columnsOrder.length ? columnsOrder : allFields;
  //   const fullOrder = [...order, ...allFields.filter(f => !order.includes(f))];
  //   return fullOrder
  //     .map(field => resolvedColumns.find(c => c.dataField === field))
  //     .filter((col): col is any => col !== undefined);
  // }, [isLocal, resolvedColumns, columnsOrder]);

  const columnsToRender = gridConfig.columns;
  if (scenario === Scenarios.DocumentDetail) {
    console.log('columnsToRender', columnsToRender);
    console.log('data', data);
  }

  const dataGridMemo = useMemo(() => {
    return (
      <DataGrid
        ref={dataGridRef}
        dataSource={data || []}
        width="100%"
        height="100%"
        showBorders
        columnHidingEnabled
        selection={{ mode: 'single' }}
        columnResizingMode="widget"
        allowColumnResizing
        allowColumnReordering
        onOptionChanged={handleOptionChanged}
        onContextMenuPreparing={onRowContextMenuPreparing}
        onToolbarPreparing={(e: any) => {
          const container = document.createElement('div');
          e.toolbarOptions.items.unshift({
            location: 'after',
            template: () => container,
          });
          const root = createRoot(container);
          root.render(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isGroupingAvailable && !isLocal && (
                <Tooltip title="Seleccionar agrupamiento">
                  <IconButton onClick={() => setGroupModalOpen(true)} size="small" sx={{ color: '#1976d2' }}>
                    <ViewAgendaIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {isConfigAvailable && (
                <Tooltip title="Configuración de columnas">
                  <IconButton onClick={() => setModalOpen(true)} size="small" sx={{ color: '#1976d2' }}>
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        }}
        grouping={{ autoExpandAll: false, expandMode: 'rowClick' }}
      >
        {!isLocal && <RemoteOperations sorting filtering paging summary grouping />}
        <Scrolling mode="virtual" rowRenderingMode="virtual" />
        {isGroupingAvailable && (
          <Summary>
            {Object.entries(summary).map(([dataField, isActive]) => isActive && (
              <GroupItem
                key={`group-${dataField}`}
                column={dataField}
                summaryType="sum"
                alignByColumn
                customizeText={(itemInfo: any) => {
                  let numericValue: number | null = null;
                  if (typeof itemInfo.value === 'number') numericValue = itemInfo.value;
                  else if (typeof itemInfo.value === 'string') {
                    const parsed = parseFloat(itemInfo.value);
                    if (!isNaN(parsed)) numericValue = parsed;
                  }
                  if (numericValue === null || isNaN(numericValue)) return '';
                  return getNumberFormat()(numericValue);
                }}
              />
            ))}
          </Summary>
        )}
        {isGroupingAvailable && <Grouping autoExpandAll={false} expandMode="rowClick" />}
        {isGroupingAvailable && (
          <GroupPanel visible emptyPanelText="Arrastre una columna aquí para agrupar" />
        )}
        <FilterRow visible />
        <HeaderFilter visible />
        {isFilterPanelAvailable && <FilterPanel visible />}
        <Sorting mode="multiple" />
        <ColumnFixing enabled texts={{ fix: 'Fijar columna', leftPosition: 'Fijar a la izquierda', rightPosition: 'Fijar a la derecha', unfix: 'Desfijar columna' }} />

        {columnsToRender.map((col: any) => {
          const isGroupable = isGroupingAvailable ? groupable[col.dataField] ?? (col.allowGrouping !== false) : false;
          const isVisible = visible[col.dataField] ?? (col.visible !== false);
          const isFixed = fixed[col.dataField] ?? false;
          const groupIdx = !isLocal && isGroupingAvailable && col.groupIndex !== undefined && col.groupIndex !== null ? col.groupIndex : undefined;

          let columnFormat = col.format;
          if (col.dataField !== 'OID' && col.dataType === 'number') {
            columnFormat = col.format === 'currency' ? getNumberFormat() : col.format || getNumberFormat();
          }

          return (
            <Column
              key={col.dataField}
              dataField={col.dataField}
              caption={col.caption}
              dataType={col.dataType}
              width={col.width}
              format={columnFormat}
              visible={isVisible}
              allowHiding={col.allowHiding !== false}
              allowGrouping={isGroupable}
              allowSorting={col.allowSorting !== false}
              allowFiltering={col.allowFiltering !== false}
              fixed={isFixed}
              groupIndex={groupIdx}
            />
          );
        })}
      </DataGrid>
    );
  }, [
    data,
    isLocal,
    columnsToRender,
    summary,
    groupable,
    visible,
    fixed,
    handleOptionChanged,
    isConfigAvailable,
    isGroupingAvailable,
    onRowContextMenuPreparing,
    activeGroup,
  ]);

  if (!isLocal && vFlexColumnsConfig === null) {
    return <Box sx={{ p: 4, textAlign: 'center' }}>Cargando configuración de columnas...</Box>;
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <style>{`
        .dx-datagrid-group-row td { overflow: visible !important; }
        .dx-datagrid-summary-item { position: relative !important; z-index: 100 !important; background-color: inherit !important; }
        .dx-datagrid-rowsview .dx-row.dx-group-row { overflow: visible !important; }
        .dx-datagrid-group-row-container { position: relative !important; z-index: 150 !important; background-color: "#F7F7F7" !important; padding-right: 120px; }
      `}</style>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {dataGridMemo}
      </Box>

      {isGroupingAvailable && !isLocal && (
        <GroupSelectorModal
          open={groupModalOpen}
          onOpenChange={setGroupModalOpen}
          showTrigger={false}
          onGroupActivated={handleGroupActivated}
          currentGroupFields={currentGroupFields}
          activeGroupCorrelative={activeGroupCorrelative}
          fieldCaptionMap={fieldCaptionMap}
          scenario={scenario}
        />
      )}

      {isConfigAvailable && (
        <GridConfigModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={handleSaveConfigFromModal}
          initialOrder={columnsOrder}
          initialVisible={visible}
          initialGroupable={groupable}
          initialSummary={summary}
          initialFixed={fixed}
          useLocalStorage={true}
          allColumns={resolvedColumns}
          activeColumns={resolvedColumns.filter(c => c.visible)}
          allowGrouping={isGroupingAvailable}
        />
      )}
    </div>
  );
};


